import { DecisionStrip } from '@/components/decisions/DecisionStrip';
import { useDashboardOverviewQuery } from '@/hooks/queries/use-dashboard-overview-query';
import { useFeatureFlagGate } from '@/hooks/use-feature-flag-gate';
import {
  DASHBOARD_DECISION_SOURCE_KEYS,
  FeatureFlag,
  type DashboardDecisions,
} from '@fluenceur/types';
import { useState } from 'react';

/**
 * A brand offered neither collabs (UGC or sponsored) nor gifting sees the strip
 * only when something is laid on it (a payout to pay): a brand offered either
 * keeps it, « Rien à décider » included.
 */
const isHidden = (decisions: DashboardDecisions) =>
  decisions.sources.ugc === 'unavailable' &&
  decisions.sources.sponsored === 'unavailable' &&
  decisions.sources.gifts === 'unavailable' &&
  decisions.days.every((day) => day.decisions.length === 0);

/**
 * The dashboard's « Vos décisions sur 14 jours »: every decision of the brand,
 * read with the to-do card from `GET /dashboard/overview`, so a gesture that
 * stales the overview refreshes both. A click lists the day's decisions under
 * the strip, each a link to where it is made; a second click closes it.
 */
export const DashboardDecisionStrip = () => {
  const { data, isLoading, refetch } = useDashboardOverviewQuery();
  // A brand offered collabs always ends with a strip: only then is a pulse a promise kept.
  const collabsGate = useFeatureFlagGate(FeatureFlag.COLLABS);
  const [selected, setSelected] = useState<string | null>(null);
  const decisions = data?.decisions;

  if (isLoading) {
    if (collabsGate !== 'granted') return null;
    return (
      <DecisionStrip
        variant="dashboard"
        days={[]}
        isLoading
        selected={null}
        onSelect={() => undefined}
      />
    );
  }
  // The whole request failing is the to-do card's to say; the strip stays out of the way.
  if (!decisions || isHidden(decisions)) return null;

  return (
    <DecisionStrip
      variant="dashboard"
      days={decisions.days}
      failedSources={DASHBOARD_DECISION_SOURCE_KEYS.filter(
        (key) => decisions.sources[key] === 'failed'
      )}
      onRetry={() => void refetch()}
      selected={selected}
      onSelect={setSelected}
    />
  );
};
